import type { OpportunitiesResponse } from '@ps/isp-web-services-types';
import { FeatureFlag } from '@ps/web-core-enums';
import { canUseDOM, canUseLocalStorage } from '@ps/web-core-utils';
import { selectionsCleared } from '@ps/web-shared-analytics';
import isBefore from 'date-fns/isBefore';

import { RequestError } from '@swf/core/errors/RequestError';
import type { EffectParams } from '@swf/core/store';
import { BET_SLIP_PERSIST_VERSION } from '@swf/core/store/middleware/persistBetSlipStateMiddleware';
import buildSportsRequest from '@swf/sports/utils/buildSportsRequest';

import { trackingContext } from './config';
import type { PersistedBetSlipState } from './types';

type PersistedStateWithExpiryAndVersion = PersistedBetSlipState & {
  exp: string;
  version: string;
};

let requestCancellationController: AbortController | undefined;

const effects = {
  async opportunitiesRequested(
    _payload: undefined,
    { dispatch, request, rootState, select }: EffectParams
  ) {
    const selections = select.betSlip.selections(rootState);

    if (!selections.length) {
      requestCancellationController?.abort();
      return;
    }

    requestCancellationController?.abort();
    requestCancellationController = new AbortController();
    const requestCancellationSignal = requestCancellationController.signal;
    const sportsRequest = buildSportsRequest(request, rootState);

    sportsRequest<OpportunitiesResponse>({
      path: '/opportunities',
      method: 'POST',
      body: selections,
      includeFeatures: [FeatureFlag.ISPBetBuilder, FeatureFlag.ISPEnableStarSub],
      signal: requestCancellationSignal
    })
      .then(({ body: latestOpportunities }) => {
        dispatch.betSlip.betSlipUpdated(latestOpportunities);
      })
      .catch((err) => {
        if (err instanceof RequestError && err.body.code !== 'ABORT_ERROR') {
          dispatch.betSlip.betSlipUpdateFailed();
        }
      });
  },

  rehydrateBetslip(_payload: unknown, { dispatch }: EffectParams) {
    if (canUseDOM && canUseLocalStorage) {
      try {
        const persistedState: PersistedStateWithExpiryAndVersion | object = JSON.parse(
          localStorage.getItem('betSlip') ?? '{}'
        );

        if (
          'exp' in persistedState &&
          isBefore(new Date(), new Date(persistedState.exp)) &&
          persistedState.version === BET_SLIP_PERSIST_VERSION
        ) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { exp, version, ...state } = persistedState;
          dispatch.betSlip.betSlipRehydrated(state);
          dispatch.betSlip.opportunitiesRequested();
        } else {
          dispatch.betSlip.betSlipRehydrated();
          localStorage.removeItem('betSlip');
        }
      } catch {
        dispatch.betSlip.betSlipRehydrated();
        localStorage.removeItem('betSlip');
      }
    }
  },

  betPlacedSuccessfully(_payload: unknown, { dispatch }: EffectParams) {
    dispatch.extraWallets.walletsRequested();
  },

  betPlacementFailed(_payload: unknown, { dispatch }: EffectParams) {
    dispatch.betSlip.opportunitiesRequested();
  },

  selectionAdded(_payload: unknown, { dispatch }: EffectParams) {
    dispatch.betSlip.opportunitiesRequested();
  },

  selectionsAdded(_payload: unknown, { dispatch }: EffectParams) {
    dispatch.betSlip.opportunitiesRequested();
  },

  selectionsCleared(_payload: unknown, { select, rootState }: EffectParams) {
    selectionsCleared(
      { ...trackingContext, isMobileEmbedded: select.device.isMobileEmbedded(rootState) },
      rootState.betSlip.opportunities.SINGLE
    );
  },

  selectionRemoved(_payload: unknown, { select, dispatch, rootState }: EffectParams) {
    if (select.betSlip.hasSelections(rootState)) {
      dispatch.betSlip.opportunitiesRequested();
    }
  },

  selectionsRemoved(_payload: unknown, { select, dispatch, rootState }: EffectParams) {
    if (select.betSlip.hasSelections(rootState)) {
      dispatch.betSlip.opportunitiesRequested();
    }
  },

  selectionsReused(_payload: unknown, { dispatch }: EffectParams) {
    dispatch.betSlip.opportunitiesRequested();
  }
};

export default effects;
